import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ludolio/shared-ui';
import { Activity, ArrowLeft, BookOpen, CheckCircle2, Settings, Users } from 'lucide-react';
import { ActivitiesTab } from './components/tabs/ActivitiesTab';
import { GamesTab } from './components/tabs/GamesTab';
import { MarkbookTab } from './components/tabs/MarkbookTab';
import { StudentsTab } from './components/tabs/StudentsTab';
import { UnitSettingsTab } from './components/tabs/UnitSettingsTab';
import { useUnitsModule } from './module-context';
import type { Activity as UnitActivity, Game, Unit, UnitStudent, UnitsUser } from './types';

interface UnitDetailViewProps {
  unitId: string;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { response?: { data?: { message?: unknown } }; message?: unknown };
    if (typeof candidate.response?.data?.message === 'string') return candidate.response.data.message;
    if (typeof candidate.message === 'string') return candidate.message;
  }
  return fallback;
}

function getSemesterLabel(semester: string) {
  const labels: Record<string, string> = {
    semester_1: 'Semester 1',
    semester_2: 'Semester 2',
    summer: 'Summer',
    winter: 'Winter',
  };
  return labels[semester] || semester;
}

export function UnitDetailView({ unitId }: UnitDetailViewProps) {
  const { api, capabilities, getRegion, navigateToUnits, reportError } = useUnitsModule();
  const [unit, setUnit] = useState<Unit | null>(null);
  const [students, setStudents] = useState<UnitStudent[]>([]);
  const [teachers, setTeachers] = useState<UnitsUser[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [activities, setActivities] = useState<UnitActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sectionLoadError, setSectionLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('students');

  const loadUnitData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setLoadError(null);
      const [unitResult, studentsResult, gamesResult, teachersResult, activitiesResult] = await Promise.allSettled([
        api.getUnitById(unitId),
        api.getUnitStudents(unitId),
        api.getUnitGames(unitId),
        capabilities.showAssignedStaff || capabilities.canManageAssignedStaff
          ? api.getUnitTeachers(unitId)
          : Promise.resolve({ success: true, data: [] }),
        api.listActivities(unitId),
      ]);

      if (unitResult.status !== 'fulfilled' || !unitResult.value.success || !unitResult.value.data) {
        throw unitResult.status === 'rejected'
          ? unitResult.reason
          : new Error(unitResult.value.error || unitResult.value.message || 'Failed to load unit');
      }

      setUnit(unitResult.value.data);
      if (studentsResult.status === 'fulfilled' && studentsResult.value.success && studentsResult.value.data) {
        setStudents(studentsResult.value.data);
      }
      if (gamesResult.status === 'fulfilled' && gamesResult.value.success && gamesResult.value.data) {
        setGames(gamesResult.value.data);
      }
      if (teachersResult.status === 'fulfilled' && teachersResult.value.success && teachersResult.value.data) {
        setTeachers(teachersResult.value.data);
      }
      setActivities(
        activitiesResult.status === 'fulfilled' && activitiesResult.value.success && activitiesResult.value.data
          ? activitiesResult.value.data
          : [],
      );

      const secondaryResults = [
        ['unit students', studentsResult],
        ['unit games', gamesResult],
        ['unit teachers', teachersResult],
        ['unit activities', activitiesResult],
      ] as const;
      const failedSections: string[] = [];
      secondaryResults.forEach(([label, result]) => {
        if (result.status === 'rejected') {
          failedSections.push(label);
          console.error(`Failed to load ${label}:`, result.reason);
          reportError(result.reason);
          return;
        }

        if (!result.value.success || !result.value.data) {
          failedSections.push(label);
          const responseError =
            'error' in result.value && typeof result.value.error === 'string'
              ? result.value.error
              : undefined;
          const responseMessage =
            'message' in result.value && typeof result.value.message === 'string'
              ? result.value.message
              : undefined;
          const error = new Error(
            responseError || responseMessage || `Failed to load ${label}`
          );
          console.error(error.message);
          reportError(error);
        }
      });
      setSectionLoadError(
        failedSections.length
          ? `Some data could not be loaded: ${failedSections.join(', ')}.`
          : null
      );
    } catch (error) {
      console.error('Failed to load unit data:', error);
      reportError(error);
      setLoadError(getErrorMessage(error, 'Failed to load unit'));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [api, capabilities.canManageAssignedStaff, capabilities.showAssignedStaff, reportError, unitId]);

  useEffect(() => {
    void loadUnitData(true);
  }, [loadUnitData]);

  const refreshUnitData = useCallback(() => loadUnitData(false), [loadUnitData]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">Loading unit...</p>
        </div>
      </div>
    );
  }

  if (!unit && loadError) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <h2 className="text-2xl font-bold">Unit could not be loaded</h2>
        <p className="mt-2 text-muted-foreground">{loadError}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={navigateToUnits}><ArrowLeft className="mr-2 h-4 w-4" />Back to Units</Button>
          <Button onClick={() => void loadUnitData()}>Try again</Button>
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="py-12 text-center">
        <h2 className="mb-2 text-2xl font-bold">Unit not found</h2>
        <Button onClick={navigateToUnits}><ArrowLeft className="mr-2 h-4 w-4" />Back to Units</Button>
      </div>
    );
  }

  const showSettings = capabilities.showAssignedStaff
    || capabilities.canManageAssignedStaff
    || capabilities.canEditMetadata
    || capabilities.canArchiveRestore
    || capabilities.canPermanentlyDelete;
  const unitRegion = getRegion?.(unit.organization?.region) ?? unit.organization?.region;

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" onClick={navigateToUnits} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />Back to Units
        </Button>
        <h1 className="text-3xl font-bold">{unit.code}</h1>
        <p className="mt-1 text-muted-foreground">{unit.name}</p>
        <div className="mt-2 flex items-center gap-2">
          <Badge>{unit.year}</Badge>
          <Badge variant="outline">{getSemesterLabel(unit.semester)}</Badge>
          {unit.isArchived && <Badge variant="secondary">Archived</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {([
          { label: 'Total Students', count: students.length, Icon: Users },
          { label: 'Assigned Games', count: games.length, Icon: BookOpen },
          { label: 'Activities', count: activities.length, Icon: Activity },
        ]).map(({ label, count, Icon }) => (
          <Card key={String(label)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{label}</CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold">{count}</div></CardContent>
          </Card>
        ))}
      </div>

      {(loadError || sectionLoadError) && (
        <Alert variant="destructive">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <AlertDescription>
              {loadError || sectionLoadError}
            </AlertDescription>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refreshUnitData()}
            >
              Try again
            </Button>
          </div>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto pb-1">
          <TabsList className="w-max min-w-full justify-start">
            <TabsTrigger value="students"><Users className="mr-2 h-4 w-4" />Students</TabsTrigger>
            <TabsTrigger value="games"><BookOpen className="mr-2 h-4 w-4" />Games</TabsTrigger>
            <TabsTrigger value="activities"><Activity className="mr-2 h-4 w-4" />Activities</TabsTrigger>
            {capabilities.canViewMarkbook && <TabsTrigger value="markbook"><CheckCircle2 className="mr-2 h-4 w-4" />Markbook</TabsTrigger>}
            {showSettings && <TabsTrigger value="settings"><Settings className="mr-2 h-4 w-4" />Settings</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="students" className="space-y-4">
          <StudentsTab unitId={unitId} students={students} onDataChanged={refreshUnitData} />
        </TabsContent>
        <TabsContent value="games">
          <GamesTab unitId={unitId} games={games} unitRegion={unitRegion} onDataChanged={refreshUnitData} />
        </TabsContent>
        <TabsContent value="activities" className="space-y-4">
          <ActivitiesTab unitId={unitId} games={games} activities={activities} onDataChanged={refreshUnitData} />
        </TabsContent>
        {capabilities.canViewMarkbook && (
          <TabsContent value="markbook"><MarkbookTab students={students} activities={activities} /></TabsContent>
        )}
        {showSettings && (
          <TabsContent value="settings" className="space-y-6">
            <UnitSettingsTab unit={unit} teachers={teachers} onDataChanged={refreshUnitData} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
